import { Trans, useLingui } from "@lingui/react/macro";
import { Card, Input } from "@rakazo/ui-web";
import type { ReactNode } from "react";
import { useEffect, useId, useRef } from "react";
import { providerRowHint } from "../../lib/localized-provider-hint";

import type { ModelSettingsState } from "./use-model-settings";

function rowClassName(current: boolean): string {
  return `flex w-full items-center gap-3 border-b border-border px-3.5 py-3 text-start last:border-0 ${
    current ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
  }`;
}

function ProviderGroup({ title, children }: { title?: ReactNode; children: ReactNode }) {
  const headingId = useId();
  return (
    <section aria-labelledby={title ? headingId : undefined} className="space-y-2">
      {title ? (
        <h3 id={headingId} className="px-1 text-sm font-semibold text-foreground">
          {title}
        </h3>
      ) : null}
      <Card className="gap-0 py-0">{children}</Card>
    </section>
  );
}

export function ProviderList({
  connectedGroups,
  filteredGroups,
  otherGroups,
  providerQuery,
  setProviderQuery,
  chooseProvider,
  credentialByProvider,
  groups,
  provider,
}: Pick<
  ModelSettingsState,
  | "connectedGroups"
  | "filteredGroups"
  | "otherGroups"
  | "providerQuery"
  | "setProviderQuery"
  | "chooseProvider"
  | "credentialByProvider"
  | "groups"
  | "provider"
>) {
  const { t } = useLingui();
  const listRef = useRef<HTMLDivElement>(null);
  // The list scrolls on its own, so a chosen provider can sit below its fold. Bring the chosen row
  // into view by moving only this list; the page around it must not shift.
  // Список прокручується сам, тож вибраний рядок може опинитися нижче видимої частини.
  useEffect(() => {
    const list = listRef.current;
    const row = list?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!list || !row) return;
    const listBox = list.getBoundingClientRect();
    const rowBox = row.getBoundingClientRect();
    if (rowBox.bottom > listBox.bottom) list.scrollTop += rowBox.bottom - listBox.bottom;
    else if (rowBox.top < listBox.top) list.scrollTop -= listBox.top - rowBox.top;
  }, [provider]);
  function renderProviderRow(group: (typeof groups)[number], connectedSection: boolean) {
    const current = group.id === provider;
    const rowCredential = credentialByProvider.get(group.id);
    const savedModelLabel = rowCredential?.modelId
      ? (group.entries.find((entry) => entry.id === rowCredential.modelId)?.label ??
        rowCredential.modelId)
      : null;
    // Plain API-key providers show only their name; a subtitle marks the exception.
    const rowSubtitle =
      connectedSection && savedModelLabel ? savedModelLabel : providerRowHint(group.entries[0]!);
    return (
      <button
        key={group.id}
        type="button"
        aria-current={current ? "true" : undefined}
        onClick={() => chooseProvider(group.id)}
        className={rowClassName(current)}
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] text-foreground">{group.name}</span>
          {rowSubtitle ? (
            <span className="mt-0.5 block truncate text-[12px] text-muted-foreground/80">
              {rowSubtitle}
            </span>
          ) : null}
        </span>
        {!connectedSection && rowCredential ? (
          <span className="text-[13px] leading-[1.5] text-success">
            <Trans>Connected</Trans>
          </span>
        ) : null}
      </button>
    );
  }

  return (
    <div className="flex shrink-0 flex-col md:w-[310px]">
      <div className="mb-3 text-[13.5px] text-muted-foreground">
        <Trans>Providers</Trans>
      </div>
      <label className="sr-only" htmlFor="model-provider-search">
        <Trans>Search providers</Trans>
      </label>
      <Input
        id="model-provider-search"
        value={providerQuery}
        onChange={(event) => setProviderQuery(event.target.value)}
        placeholder={t`Search providers`}
        className="h-10 rounded-xl px-3.5"
      />
      <div
        ref={listRef}
        className="rk-scroll mt-3 max-h-[240px] space-y-5 overflow-y-auto p-px md:max-h-[480px]"
      >
        {filteredGroups.length ? (
          <>
            {connectedGroups.length ? (
              <ProviderGroup title={<Trans>Connected</Trans>}>
                {connectedGroups.map((group) => renderProviderRow(group, true))}
              </ProviderGroup>
            ) : null}
            {otherGroups.length ? (
              <ProviderGroup
                title={connectedGroups.length ? <Trans>All providers</Trans> : undefined}
              >
                {otherGroups.map((group) => renderProviderRow(group, false))}
              </ProviderGroup>
            ) : null}
          </>
        ) : (
          <Card className="gap-0 px-3.5 py-4 text-[13px] leading-[1.5] text-muted-foreground">
            <Trans>No providers found.</Trans>
          </Card>
        )}
      </div>
    </div>
  );
}
